import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TextInput from "../../components/common/TextInput";
import Button from "../../components/common/Button";
import { loginUser } from "../../api/authApi";
import { useAuth } from "../../hooks/useAuth";
import { FiUser, FiLock, FiEye, FiEyeOff } from "react-icons/fi";

function LoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage(null);

    if (!identifier || !password) {
      setError("Username and password are required.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        identifier: identifier.trim(),
        password,
      };

      const data = await loginUser(payload);

      // Check if login was successful
      if (data.success === false) {
        setError(data.message || "Login failed. Please try again.");
        return;
      }

      // Login successful
      setSuccessMessage(data.message || "Login successful!");

      // Store auth token and user data
      if (data.auth_token && data.user) {
        login(data.auth_token, data.user);

        // Navigate to home page after short delay
        setTimeout(() => {
          navigate("/", { replace: true });
        }, 1000);
      } else {
        setError("Invalid response from server. Please try again.");
      }
    } catch (err) {
      console.error("Login error:", err);

      if (err.response) {
        // Server responded with error status
        const { status, data } = err.response;

        if (status === 400) {
          setError(data.message || "Invalid input. Please check your credentials.");
        } else if (status === 404) {
          setError(data.message || "User not found. Please check your username.");
        } else if (status === 401) {
          setError(data.message || "Invalid password. Please try again.");
        } else if (status === 403) {
          setError(data.message || "Your account is blocked. Please contact support.");
        } else if (status === 500) {
          setError(data.message || "Server error. Please try again later.");
        } else {
          setError(data.message || "Login failed. Please try again.");
        }
      } else if (err.request) {
        // Request was made but no response received
        setError("Unable to connect to server. Please check your internet connection.");
      } else {
        // Something else happened
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-white to-secondary-50 px-4">
      <div className="w-full max-w-md animate-slideUp">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full opacity-20 blur-lg"></div>
              <img
                src="/logo.png"
                alt="Review Market EU"
                className="relative h-16 w-16 object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gradient mb-2">
            Review Market EU
          </h1>
          <p className="text-gray-600">Welcome back! Please login to continue</p>
        </div>

        {/* Login Card */}
        <div className="card-gradient">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Login</h2>

          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 border border-red-200 flex items-start gap-2 animate-fadeIn">
              <svg className="h-5 w-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700 border border-green-200 flex items-start gap-2 animate-fadeIn">
              <svg className="h-5 w-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{successMessage}</span>
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <TextInput
              label="User Name"
              name="identifier"
              value={identifier}
              onChange={(e) => {
                setIdentifier(e.target.value);
                setError(""); // Clear error on input change
              }}
              placeholder="Enter your username"
              icon={<FiUser className="h-5 w-5" />}
              required
              autoComplete="username"
            />

            <div className="relative">
              <TextInput
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(""); // Clear error on input change
                }}
                placeholder="Enter your password"
                icon={<FiLock className="h-5 w-5" />}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[42px] text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? (
                  <FiEyeOff className="h-5 w-5" />
                ) : (
                  <FiEye className="h-5 w-5" />
                )}
              </button>
            </div>

            <Button type="submit" className="w-full" loading={loading}>
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{" "}
              <Link
                to="/register"
                className="font-semibold text-primary-600 hover:text-primary-700 transition-colors"
              >
                Create account
              </Link>
            </p>
          </div>
        </div>

        {/* Footer Text */}
        <p className="mt-6 text-center text-xs text-gray-500">
          By logging in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
